import React, { useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Button, Box, Menu, MenuItem, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser, userProfile, loading, authInitialized } = useAuth();
  const token = localStorage.getItem('token');
  const [user, setUser] = useState<User | null>(null);

  // State for Access Management menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Add state for Codes dropdown
  const [codesAnchorEl, setCodesAnchorEl] = useState<null | HTMLElement>(null);
  const codesOpen = Boolean(codesAnchorEl);

  // Add state for Admin dropdown
  const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
  const adminOpen = Boolean(adminAnchorEl);

  // Add timeout refs for delayed closing
  const accessManagementTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const codesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const adminTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to check if a route is active
  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  // Helper function to check if a dropdown route is active
  const isActiveDropdownRoute = (paths: string[]) => {
    return paths.some(path => location.pathname === path);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (accessManagementTimeoutRef.current) {
        clearTimeout(accessManagementTimeoutRef.current);
      }
      if (codesTimeoutRef.current) {
        clearTimeout(codesTimeoutRef.current);
      }
      if (adminTimeoutRef.current) {
        clearTimeout(adminTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) return;
      try {
        const data = await apiService.getUserMe();
        setUser(data);
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    fetchUser();
  }, [token]);

  // Don't render navigation if not authenticated or still loading
  if (!authInitialized || loading || !currentUser) {
    return <Outlet />;
  }

  const handleUsersClick = () => {
    navigate('/users');
    setAnchorEl(null);
  };

  // Menu-only hover handlers to prevent button/menu conflicts
  const handleAccessManagementMenuLeave = () => {
    accessManagementTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 300);
  };

  const handleAdminMenuLeave = () => {
    adminTimeoutRef.current = setTimeout(() => {
      setAdminAnchorEl(null);
    }, 300);
  };

  const handleCodesMenuLeave = () => {
    codesTimeoutRef.current = setTimeout(() => {
      setCodesAnchorEl(null);
    }, 300);
  };

  // Universal menu mouse enter handler - clears all timeouts
  const handleMenuMouseEnter = () => {
    if (accessManagementTimeoutRef.current) {
      clearTimeout(accessManagementTimeoutRef.current);
      accessManagementTimeoutRef.current = null;
    }
    if (codesTimeoutRef.current) {
      clearTimeout(codesTimeoutRef.current);
      codesTimeoutRef.current = null;
    }
    if (adminTimeoutRef.current) {
      clearTimeout(adminTimeoutRef.current);
      adminTimeoutRef.current = null;
    }
  };

  const handleLogout = async () => {
    try {
      // Clear all local storage and session storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear any cached data in memory
      setUser(null);
      
      // Clear any browser caches if possible
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        } catch (cacheError) {
          console.warn('Could not clear browser caches:', cacheError);
        }
      }
      
      // Clear any IndexedDB data if present
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          databases.forEach(db => {
            if (db.name) {
              indexedDB.deleteDatabase(db.name);
            }
          });
        } catch (indexedDBError) {
          console.warn('Could not clear IndexedDB:', indexedDBError);
        }
      }
      
      // Use the AuthContext logout function to properly sign out from Firebase
      await logout();
      
      // Force a page reload to clear any remaining React state
      window.location.href = '/';
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, still clear storage and force reload
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  // Common button styling with better contrast for active state
  const getButtonSx = (isActive: boolean) => ({
    borderColor: 'rgba(255, 255, 255, 0.3)',
    textDecoration: isActive ? 'underline' : 'none',
    backgroundColor: isActive ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
    color: 'white',
    '&:hover': {
      backgroundColor: isActive ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
    }
  });

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/dashboard"
              sx={getButtonSx(isActiveRoute('/dashboard'))}
            >
              Dashboard
            </Button>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/orders"
              sx={getButtonSx(isActiveRoute('/orders'))}
            >
              Orders
            </Button>
            {/* Access Management Dropdown */}
            <Button
              variant="outlined"
              color="inherit"
              onClick={e => setAnchorEl(e.currentTarget)}
              aria-controls={open ? 'access-mgmt-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              sx={getButtonSx(isActiveDropdownRoute(['/users', '/roles']))}
            >
              Access Management
            </Button>
            <Menu
              id="access-mgmt-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{ 
                'aria-labelledby': 'access-mgmt-button',
                onMouseEnter: handleMenuMouseEnter,
                onMouseLeave: handleAccessManagementMenuLeave,
              }}
              slotProps={{
                paper: {
                  onMouseEnter: handleMenuMouseEnter,
                  onMouseLeave: handleAccessManagementMenuLeave,
                }
              }}
            >
              <MenuItem onClick={handleUsersClick}>Users</MenuItem>
              <MenuItem onClick={() => { navigate('/roles'); setAnchorEl(null); }}>Roles</MenuItem>
            </Menu>
            {/* Admin Dropdown */}
            <Button
              variant="outlined"
              color="inherit"
              onClick={e => setAdminAnchorEl(e.currentTarget)}
              aria-controls={adminOpen ? 'admin-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={adminOpen ? 'true' : undefined}
              sx={getButtonSx(isActiveDropdownRoute(['/admin/specimen-classes', '/admin/partner-integration']))}
            >
              Admin
            </Button>
            <Menu
              id="admin-menu"
              anchorEl={adminAnchorEl}
              open={adminOpen}
              onClose={() => setAdminAnchorEl(null)}
              MenuListProps={{ 
                'aria-labelledby': 'admin-button',
                onMouseEnter: handleMenuMouseEnter,
                onMouseLeave: handleAdminMenuLeave,
              }}
              slotProps={{
                paper: {
                  onMouseEnter: handleMenuMouseEnter,
                  onMouseLeave: handleAdminMenuLeave,
                }
              }}
            >
              <MenuItem onClick={() => { navigate('/admin/specimen-classes'); setAdminAnchorEl(null); }}>Admin Templates</MenuItem>
              <MenuItem onClick={() => { navigate('/admin/partner-integration'); setAdminAnchorEl(null); }}>Partner Integration</MenuItem>
            </Menu>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/locations"
              sx={getButtonSx(isActiveRoute('/locations'))}
            >
              Locations
            </Button>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/patients"
              sx={getButtonSx(isActiveRoute('/patients'))}
            >
              Patients
            </Button>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/automation"
              sx={getButtonSx(isActiveRoute('/automation'))}
            >
              Automation
            </Button>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/macros"
              sx={getButtonSx(isActiveRoute('/macros'))}
            >
              Macros
            </Button>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/reports"
              sx={getButtonSx(isActiveRoute('/reports'))}
            >
              Reports
            </Button>
            <Button 
              variant="outlined"
              color="inherit" 
              component={RouterLink} 
              to="/billing"
              sx={getButtonSx(isActiveRoute('/billing'))}
            >
              Billing
            </Button>
            {/* Codes Dropdown */}
            <Button
              variant="outlined"
              color="inherit"
              onClick={e => setCodesAnchorEl(e.currentTarget)}
              aria-controls={codesOpen ? 'codes-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={codesOpen ? 'true' : undefined}
              sx={getButtonSx(isActiveDropdownRoute(['/codes/cpt', '/codes/snomed', '/codes/icd10', '/codes/specimen-mapping', '/codes/diagnosis-mapping', '/codes/modifiers']))}
            >
              Codes
            </Button>
            <Menu
              id="codes-menu"
              anchorEl={codesAnchorEl}
              open={codesOpen}
              onClose={() => setCodesAnchorEl(null)}
              MenuListProps={{ 
                'aria-labelledby': 'codes-button',
                onMouseEnter: handleMenuMouseEnter,
                onMouseLeave: handleCodesMenuLeave,
              }}
              slotProps={{
                paper: {
                  onMouseEnter: handleMenuMouseEnter,
                  onMouseLeave: handleCodesMenuLeave,
                }
              }}
            >
              <MenuItem onClick={() => { navigate('/codes/cpt'); setCodesAnchorEl(null); }}>CPT</MenuItem>
              <MenuItem onClick={() => { navigate('/codes/snomed'); setCodesAnchorEl(null); }}>SNOMED</MenuItem>
              <MenuItem onClick={() => { navigate('/codes/icd10'); setCodesAnchorEl(null); }}>ICD-10</MenuItem>
              <MenuItem onClick={() => { navigate('/codes/modifiers'); setCodesAnchorEl(null); }}>Modifiers</MenuItem>
              <MenuItem onClick={() => { navigate('/codes/specimen-mapping'); setCodesAnchorEl(null); }}>Specimen Mapping</MenuItem>
              <MenuItem onClick={() => { navigate('/codes/diagnosis-mapping'); setCodesAnchorEl(null); }}>Diagnosis Code Mapping</MenuItem>
            </Menu>
          </Box>
          {(token || currentUser) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {(userProfile || user) && (
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {(userProfile?.name || user?.name)} ({(userProfile?.email || user?.email)})
                </Typography>
              )}
              <Button 
                variant="outlined"
                color="inherit" 
                component={RouterLink} 
                to="/my-account"
                sx={getButtonSx(isActiveRoute('/my-account'))}
              >
                My Account
              </Button>
              <Button 
                variant="outlined"
                color="inherit" 
                onClick={handleLogout}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  }
                }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Outlet />
    </>
  );
};

export default Layout; 